<template>
  <v-container fluid class="fill-height auth-shell pa-0">
    <v-row class="fill-height auth-view" no-gutters align="stretch">
      <v-col cols="12" md="6" class="d-flex align-center justify-center pa-6">
        <div class="auth-panel">
          <router-link class="text-decoration-none text-medium-emphasis d-inline-flex align-center mb-8"
            :to="{ name: 'login' }">
            <v-icon size="18" class="mr-1">mdi-chevron-left</v-icon>
            Back to login
          </router-link>

          <v-alert
            variant="tonal"
            rounded="lg"
            :color="errorMessage ? 'danger-color' : 'brand-color'"
            class="pa-0 overflow-hidden"
          >
            <div class="px-4 py-3 d-flex align-center ga-3">
              <v-icon :color="errorMessage ? 'danger-color' : 'brand-color'">
                {{ errorMessage ? 'mdi-alert-circle' : 'mdi-login' }}
              </v-icon>
              <span class="font-weight-medium">
                {{ errorMessage || 'Please hold on while we securely log you into your account.' }}
              </span>
            </div>
            <v-progress-linear v-if="loading" indeterminate :color="errorMessage ? 'danger-color' : 'brand-color'" height="3" />
          </v-alert>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="login-hero d-none d-md-flex pa-0 align-center justify-center">
        <AuthHero :testimonial="testimonial" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AuthHero from '@/components/authentication/AuthHero.vue'
import jwtDecode from 'jwt-decode'
import * as actionTypes from '@/store/action-types.js'
import * as Auth from '@/api/auth'

export default {
  components: {
    AuthHero
  },
  data: function () {
    return {
      token: '',
      loading: true,
      errorMessage: '',
      testimonial: {
        mg: 'The principle of personalization to improve conversions is a very sound one. It applies to email, social media, and websites. What CliqForms platform enables is personalization directly within lead generation forms. That’s the key reason we consistently see much higher conversion rates for our clients using CliqForms compared to any other form.',
        role: 'Rayan Jawad, Growth Studio Co-Founder',
        img: require('@/assets/images/auth/testimonial3.png')
      }
    }
  },
  mounted: function () {
    this.token = this.$route.query.token
    this.logout()
    this.login()
  },
  methods: {
    logout: function () {
      this.$store.dispatch(actionTypes.LOGOUT_USER)
    },
    login: async function () {
      if (!this.token || !this.token.trim().length) {
        this.loading = false
        this.errorMessage = 'Unable to login due to missing token!'
        return
      }

      let decodedToken = null

      try {
        decodedToken = jwtDecode(this.token)
      } catch (e) {
        this.loading = false
        this.errorMessage = 'Token received is not a valid JWT Token.'
        return
      }

      localStorage.setItem('token', JSON.stringify(this.token))

      try {
        await Auth.setAuthCookie()
      } catch (error) {
        localStorage.setItem('token', null)
        this.loading = false
        this.errorMessage = 'Unable to login, something went wrong.'
        return
      }

      // Check localStorage for user with subscription (from Register.vue)
      const storedUserStr = localStorage.getItem('user')
      let storedUserWithSubscription = null
      if (storedUserStr && storedUserStr !== 'null') {
        try {
          const parsed = JSON.parse(storedUserStr)
          if (parsed?.plan?.subscription?.id && parsed?.id === decodedToken.sub) {
            storedUserWithSubscription = parsed
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      this.$store.dispatch(actionTypes.FETCH_USER, {
        userId: decodedToken.sub
      }).then((response) => {
        let user = response.data.data

        // If API doesn't have subscription but localStorage does, use localStorage version
        if (!user?.plan?.subscription?.id && storedUserWithSubscription?.plan?.subscription?.id) {
          user.plan = user.plan || {}
          user.plan.subscription = storedUserWithSubscription.plan.subscription
          if (!user.plan.id && storedUserWithSubscription.plan.id) {
            user.plan.id = storedUserWithSubscription.plan.id
          }
        }

        this.$store.dispatch(actionTypes.LOGIN_SUCCESS, {
          'token': this.token,
          'user': user,
          'decodedToken': decodedToken
        })

        localStorage.setItem('decodedToken', JSON.stringify(jwtDecode(this.token)))
        localStorage.setItem('user', JSON.stringify(user))

        this.fetchPlans(user)

        this.$router.push({ name: 'dashboard' })
      }, (response) => {
        localStorage.removeItem('token')
        this.errorMessage = 'Unable to login, something went wrong.'
        this.$store.dispatch(actionTypes.LOGIN_FAILED)
      })
    },
    fetchUserSubscription: function (user) {
      // Skip if user already has subscription with id (from Register.vue fetch)
      if (user?.plan?.subscription?.id) {
        return
      }

      if (!user?.plan?.id) {
        return
      }

      this.$store.dispatch(actionTypes.FETCH_PLAN_SUBSCRIPTION, {
        planId: user.plan.id
      }).then((response) => {
        user.plan.subscription = response.data.data
        localStorage.setItem('user', JSON.stringify(user))
        this.$store.dispatch(actionTypes.FETCH_PLAN_SUBSCRIPTION_SUCCESS, response.data.data)
      })
    },
    fetchPlans: function (user) {
      this.$store.dispatch(actionTypes.FETCH_PLANS).then((response) => {
        localStorage.setItem('plans', JSON.stringify(response.data.data) || [])
        this.$store.dispatch(actionTypes.FETCH_PLANS_SUCCESS, response.data.data)
        this.fetchUserSubscription(user)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.token-screen {
  min-height: 100vh;
  font-family: $heading-font-stack;
}
</style>
